import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ButtonDecorator } from '../../../components/ButtonDecorator/ButtonDecorator';
import { Bem } from '../../../service/Bem/Bem';
import { ValidationErrorData } from '../../../typings/common';
import { PcodeRenderDataCanvas } from '../../../typings/pcode';
import { Page } from '../../common/typings';
import {
    CommonCreativesSelector,
    CommonCreativesSelectorProps
} from '../../CommonCreativesSelector/CommonCreativesSelector';
import { CreativeImageCropModalContainer } from '../../CreativeImageCropModal/CreativeImageCropModalContainer';
import { CreativeOptionsData } from '../../CreativeOptions/typings';
import { CreativeSizesList, CreativeSizesListProps } from '../../CreativeSizesList/CreativeSizesList';
import { CreativeSize } from '../../CreativeSizesList/typings';
import { PcodeCanvasPreview } from '../../PcodeCanvasPreview/PcodeCanvasPreview';
import { AddCreativePage } from '../AddCreativePage';
import { GraphicPreview } from '../GraphicPreview/GraphicPreview';
import { CommonCreativeBaseItem, CommonCreativeItem } from '../typings';

import './AddCommonCreative.css';
import * as keyset from './AddCommonCreative.i18n';

const i18n = i18nFactory(keyset);

export interface AddCommonCreativeProps {
    isLoading: boolean;
    isSaving: boolean;

    creativeOptions?: CreativeOptionsData;
    onSaveName: (name: string) => void;
    onOptionChange: (groupName: string, name: string, value: string) => void;
    onGroupVisibleChange: (groupName: string) => void;

    returnPage?: Page;
    onReturnClick: (page?: Page) => void;
    error?: ValidationErrorData;
    onSave: () => void;
    onCancel: () => void;

    previewsModalVisible?: boolean;
    afterCrop?: boolean;
    createPressed?: boolean;
    previewItems?: CommonCreativeBaseItem[];

    showPreviewsModal: () => void;
    cancelPreviewsModal: () => void;
    closePreviewsModal: () => void;
    submitPreviewsModal: (continueEdit?: boolean) => void;
    togglePreview: (ids: number[], val: boolean) => void;
    crop: (id: number) => void;
    cropModalClose: () => void;

    currentId?: number;
    items?: CommonCreativeItem[];
    pcodeData?: PcodeRenderDataCanvas;

    changeSize: (value: number) => void;
    checkSize: (value: number) => void;

    initCommonData: () => void;
}

const BLOCK = 'add-common-creative';

const renderLeftPane = (props: CreativeSizesListProps): React.ReactNode => (
    <CreativeSizesList {...props} checkboxEnabled={true} />
);

const renderBottomPane = (selectedCreatives: number, showAllPreviews: () => void): React.ReactNode => (
    <div className={Bem.cls(BLOCK, 'bottom-pane')}>
        {i18n('creative-to-create', { count: selectedCreatives })}
        <ButtonDecorator
            cls={Bem.cls(BLOCK, 'show-all-button')}
            theme="normal"
            size="xs"
            text={i18n('show-all-previews')}
            onClick={showAllPreviews}
        />
    </div>
);

const getSizes = (items: CommonCreativeItem[]): CreativeSize[] => {
    return items.map(item => ({
        id: item.id,
        checked: item.selected,
        width: item.data.width,
        height: item.data.height
    }));
};

const getPreviewModalProps = (
    toggle: (ids: number[], val: boolean) => void,
    crop: (id: number) => void,
    continueEdit: () => void,
    afterCrop?: boolean,
    createPressed?: boolean
): Pick<
    CommonCreativesSelectorProps,
    'submitText' | 'cancelText' | 'toggle' | 'crop' | 'previewControls' | 'additionalCancel' | 'additionalCancelText'
> => {
    if (createPressed && afterCrop) {
        return {
            submitText: i18n('apply'),
            cancelText: i18n('crop-again'),
            crop,
            additionalCancel: continueEdit,
            additionalCancelText: i18n('continue-edit')
        };
    }

    if (afterCrop) {
        return {
            submitText: i18n('apply'),
            cancelText: i18n('crop-again'),
            crop
        };
    }

    return {
        submitText: i18n('previews-save'),
        cancelText: i18n('previews-cancel'),
        toggle
    };
};

const EMPTY_ITEMS: CommonCreativeItem[] = [];

export const AddCommonCreative = React.memo((props: AddCommonCreativeProps) => {
    const {
        currentId,
        items,
        changeSize,
        checkSize,
        showPreviewsModal,
        submitPreviewsModal,
        cancelPreviewsModal,
        togglePreview,
        previewsModalVisible,
        afterCrop,
        crop,
        initCommonData,
        pcodeData,
        previewItems,
        closePreviewsModal,
        createPressed,
        cropModalClose,
        ...addCreativeProps
    } = props;

    useEffect(() => initCommonData(), []);
    const currentItem = useMemo(
        () => (items && currentId !== undefined ? items.find(el => el.id === currentId) : undefined),
        [currentId, items]
    );
    const selectedItems = items ? items.filter(item => item.selected) : EMPTY_ITEMS;

    const previewRef = useRef<PcodeCanvasPreview>(null);
    const preview = useMemo(
        () => (
            <GraphicPreview
                item={currentItem}
                pcodeData={pcodeData}
                ref={previewRef}
                cls={Bem.cls(BLOCK, 'preview-container')}
            />
        ),
        [pcodeData, currentItem]
    );

    const leftPane = useMemo(
        () =>
            items && currentId !== undefined
                ? renderLeftPane({
                      selectedSize: currentId,
                      onChangeSize: changeSize,
                      onCheckSize: checkSize,
                      sizes: getSizes(items)
                  })
                : null,
        [currentId, items]
    );
    const bottomPane = useMemo(() => renderBottomPane(selectedItems.length, showPreviewsModal), [selectedItems.length]);
    const continueEdit = useCallback(() => submitPreviewsModal(createPressed), [createPressed]);

    return (
        <>
            <AddCreativePage
                {...addCreativeProps}
                isDocCenterTooltip={false}
                submitEnabled={Boolean(selectedItems.length)}
                preview={preview}
                leftPane={leftPane}
                bottomPane={bottomPane}
                contentCls={Bem.cls(BLOCK, 'content')}
                previewCls={Bem.cls(BLOCK, 'preview')}
            />
            {previewsModalVisible && previewItems && (
                <CommonCreativesSelector
                    cls={Bem.cls(BLOCK, 'previews-modal')}
                    header={i18n('previews-title')}
                    items={previewItems}
                    submit={submitPreviewsModal}
                    cancel={cancelPreviewsModal}
                    close={closePreviewsModal}
                    link={false}
                    showId={false}
                    sizeFilter={false}
                    {...getPreviewModalProps(togglePreview, crop, continueEdit, afterCrop, createPressed)}
                />
            )}

            <CreativeImageCropModalContainer onClose={cropModalClose} />
        </>
    );
});
